/**
 * WarnlistePage — Warnlisten-Hauptseite (LIST-01/02/03/05/06, PROT-02, Plan 02-05
 * Task 3). Laedt `GET /programmes/artikelleichen/kandidaten` mit Filter-State
 * (inkl. Aktiv-Status-Dropdown, LIST-06), rendert die Kategorie-Gruppen, haelt die
 * Mehrfachauswahl (Map von itemId -> Zeile), oeffnet Snooze-/Whitelist-Dialoge, bietet
 * den Excel-Export und startet den Scan ueber `useJobStatus(null)` (PFLICHT-Muster,
 * KEIN eigenes Polling — E54).
 *
 * Kein Button zum endgueltigen Entfernen aus Plenty oder zum Umschalten des Aktiv-Flags
 * — folgt erst in Phase 3 (PITFALLS.md Pitfall 6).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { RefreshCw, Download, PlayCircle } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { useJobStatus } from '@libero/core/hooks/useJobStatus';
import { useAuthStore } from '@libero/core/stores/authStore';
import { Button } from '@libero/core/components/ui/button';
import { Input } from '@libero/core/components/ui/input';
import KategorieGruppeComponent from '../components/KategorieGruppe';
import SnoozeDialog from '../components/SnoozeDialog';
import WhitelistDialog from '../components/WhitelistDialog';
import AktionenBar from '../components/AktionenBar';
import LoeschBestaetigungDialog from '../components/LoeschBestaetigungDialog';
import QueueStatusPanel from '../components/QueueStatusPanel';
import type {
  AktivFilter,
  AnzeigeStatus,
  BatchStatus,
  KandidatFilterState,
  KandidatRow,
  KandidatlisteResponse,
  KategorieGruppe,
  LeichenTyp,
} from '../types';
import { standFmt } from '../lib/format';

const LEICHEN_TYP_OPTIONEN: Array<{ value: LeichenTyp | ''; label: string }> = [
  { value: '', label: 'Alle Leichen-Typen' },
  { value: 'keinMandant', label: 'Kein Mandant' },
  { value: 'keinBestand', label: 'Kein Bestand ≥90 T' },
  { value: 'keinVerkauf', label: 'Kein Verkauf ≥365 T' },
];

const STATUS_OPTIONEN: Array<{ value: AnzeigeStatus | ''; label: string }> = [
  { value: '', label: 'Alle Status' },
  { value: 'neu', label: 'Neu' },
  { value: 'offen', label: 'Offen' },
  { value: 'gesnoozed', label: 'Gesnoozed' },
  { value: 'behoben', label: 'Behoben' },
];

/** LIST-06: Default "Alle" (User-Entscheidung 11.09.2026). Label "Aktiv-Status" grenzt
 * sich bewusst vom Anzeige-Status-Select ("Status") ab. */
const AKTIV_FILTER_OPTIONEN: Array<{ value: AktivFilter; label: string }> = [
  { value: 'alle', label: 'Alle' },
  { value: 'aktiv', label: 'Nur aktive' },
  { value: 'inaktiv', label: 'Nur inaktive' },
];

const DEFAULT_FILTER: KandidatFilterState = {
  leichenTyp: '',
  kategorieId: '',
  suche: '',
  status: '',
  gesnoozteAnzeigen: false,
  aktivFilter: 'alle',
};

export default function WarnlistePage() {
  const isAdmin = useAuthStore((s) => s.isAdmin());
  const [kategorien, setKategorien] = useState<KategorieGruppe[]>([]);
  const [registrySnapshotStand, setRegistrySnapshotStand] = useState<string | null>(null);
  const [gesamtTreffer, setGesamtTreffer] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [kategorieOptionen, setKategorieOptionen] = useState<Array<{ kategorieId: number | null; name: string }>>([]);

  const [leichenTyp, setLeichenTyp] = useState<KandidatFilterState['leichenTyp']>(DEFAULT_FILTER.leichenTyp);
  const [kategorieId, setKategorieId] = useState<string>(DEFAULT_FILTER.kategorieId);
  const [suche, setSuche] = useState(DEFAULT_FILTER.suche);
  const [status, setStatus] = useState<KandidatFilterState['status']>(DEFAULT_FILTER.status);
  const [gesnoozteAnzeigen, setGesnoozteAnzeigen] = useState(DEFAULT_FILTER.gesnoozteAnzeigen);
  const [aktivFilter, setAktivFilter] = useState<AktivFilter>(DEFAULT_FILTER.aktivFilter);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Map<number, KandidatRow>>(new Map());

  const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false);
  const [snoozeDialogItemIds, setSnoozeDialogItemIds] = useState<number[]>([]);
  const [whitelistDialogOpen, setWhitelistDialogOpen] = useState(false);
  const [whitelistDialogItemIds, setWhitelistDialogItemIds] = useState<number[]>([]);
  const [loeschDialogOpen, setLoeschDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const filter = useMemo<KandidatFilterState>(
    () => ({ leichenTyp, kategorieId, suche, status, gesnoozteAnzeigen, aktivFilter }),
    [leichenTyp, kategorieId, suche, status, gesnoozteAnzeigen, aktivFilter],
  );

  function buildParams(f: KandidatFilterState): Record<string, string> {
    const params: Record<string, string> = {};
    if (f.leichenTyp) params.leichenTyp = f.leichenTyp;
    if (f.status) params.status = f.status;
    if (f.kategorieId !== '') params.kategorieId = f.kategorieId;
    if (f.suche.trim()) params.suche = f.suche.trim();
    if (f.gesnoozteAnzeigen) params.gesnoozteAnzeigen = 'true';
    // LIST-06: aktivFilter nur mitsenden, wenn nicht Default "Alle" (fehlender Param = Backend-Default).
    if (f.aktivFilter !== 'alle') params.aktivFilter = f.aktivFilter;
    return params;
  }

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = buildParams(filter);

    return api
      .get('/programmes/artikelleichen/kandidaten', { params })
      .then((resp: { data: { success: boolean; data: KandidatlisteResponse } }) => {
        if (!resp.data.success) return;
        setKategorien(resp.data.data.kategorien);
        setRegistrySnapshotStand(resp.data.data.registrySnapshotStand);
        setGesamtTreffer(resp.data.data.gesamtTreffer);
        setKategorieOptionen(resp.data.data.kategorieOptionen);
      })
      .catch((err: any) => {
        setKategorien([]);
        setError(err?.response?.data?.error?.message || err?.message || 'Warnliste konnte nicht geladen werden.');
      })
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  function toggleGroupExpanded(gruppe: KategorieGruppe) {
    const key = gruppe.kategorieId === null ? 'null' : String(gruppe.kategorieId);
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleItem(row: KandidatRow) {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.itemId)) next.delete(row.itemId);
      else next.set(row.itemId, row);
      return next;
    });
  }

  function toggleGroupSelection(gruppe: KategorieGruppe, select: boolean) {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const row of gruppe.kandidaten) {
        if (select) next.set(row.itemId, row);
        else next.delete(row.itemId);
      }
      return next;
    });
  }

  function openBulkSnooze() {
    setActionError(null);
    setSnoozeDialogItemIds(Array.from(selected.keys()));
    setSnoozeDialogOpen(true);
  }

  function openEinzelSnooze(itemId: number) {
    setActionError(null);
    setSnoozeDialogItemIds([itemId]);
    setSnoozeDialogOpen(true);
  }

  function openBulkWhitelist() {
    setActionError(null);
    setWhitelistDialogItemIds(Array.from(selected.keys()));
    setWhitelistDialogOpen(true);
  }

  function openEinzelWhitelist(itemId: number) {
    setActionError(null);
    setWhitelistDialogItemIds([itemId]);
    setWhitelistDialogOpen(true);
  }

  async function handleSnoozeConfirm(kommentar: string, wiedervorlageAm: string | null) {
    await api.post('/programmes/artikelleichen/snooze', { itemIds: snoozeDialogItemIds, kommentar, wiedervorlageAm });
    setSelected(new Map());
    await load();
  }

  /** POST /whitelist nimmt nur EIN itemId entgegen — Bulk laeuft sequenziell (siehe WhitelistDialog.tsx). */
  async function handleWhitelistConfirm(kommentar: string) {
    for (const itemId of whitelistDialogItemIds) {
      await api.post('/programmes/artikelleichen/whitelist', { itemId, kommentar });
    }
    setSelected(new Map());
    await load();
  }

  async function handleAufheben(itemId: number) {
    setActionError(null);
    try {
      await api.delete('/programmes/artikelleichen/snooze', { data: { itemIds: [itemId] } });
      await load();
    } catch (err: any) {
      setActionError(err?.response?.data?.error?.message || err?.message || 'Snooze konnte nicht aufgehoben werden.');
    }
  }

  async function handleExport() {
    setExporting(true);
    setActionError(null);
    try {
      const params = buildParams(filter);
      const resp = await api.get('/programmes/artikelleichen/kandidaten/export.xlsx', { params, responseType: 'blob' });
      const url = URL.createObjectURL(resp.data as Blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = 'artikelleichen-liste.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch (err: any) {
      setActionError(err?.response?.data?.error?.message || err?.message || 'Export fehlgeschlagen.');
    } finally {
      setExporting(false);
    }
  }

  // "Jetzt pruefen" (SCAN-07) — PFLICHT-Muster useJobStatus(null): KEIN eigenes Polling
  // (E54). Nach Lauf-Ende (running -> nicht mehr running) wird die Liste neu geladen.
  const { isRunning, progress, startJob, error: jobError } = useJobStatus(null);
  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (wasRunningRef.current && !isRunning) {
      load();
    }
    wasRunningRef.current = isRunning;
  }, [isRunning, load]);

  async function handleJetztPruefen() {
    setActionError(null);
    await startJob('/programmes/artikelleichen/scan', {});
  }

  // Deaktivieren/Loeschen (ACT-01/02) — gleicher useJobStatus wie der Scan, deshalb
  // deckt der bestehende Fortschrittsbalken oben (isRunning && progress) auch diese
  // Aktionen ab. Nur fuer Admins sichtbar (Bar wird unten conditional gerendert,
  // PROT-05 UX-Gate) — das Backend erzwingt manage:artikelleichen zusaetzlich.
  async function handleDeaktivieren() {
    setActionError(null);
    await startJob('/programmes/artikelleichen/aktionen/deaktivieren', { itemIds: Array.from(selected.keys()) });
    setSelected(new Map());
  }

  /** Wird vom LoeschBestaetigungDialog erst aufgerufen, wenn das Pflichtwort LÖSCHEN stimmt. */
  async function handleLoeschenConfirm(bestaetigung: string) {
    setActionError(null);
    await startJob('/programmes/artikelleichen/aktionen/loeschen', {
      itemIds: Array.from(selected.keys()),
      bestaetigung,
    });
    setSelected(new Map());
    setLoeschDialogOpen(false);
  }

  /** Manueller Resume eines unterbrochenen Batches (ACT-05) — kein Auto-Boot-Resume. */
  async function handleFortsetzen(batch: BatchStatus) {
    setActionError(null);
    await startJob('/programmes/artikelleichen/aktionen/' + batch.aktion, { batchId: batch.batchId });
  }

  const selectedCount = selected.size;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Artikelleichen — Warnliste</h1>
          <p className="text-sm text-gray-500 mt-1">
            Stand: {standFmt(registrySnapshotStand)} · {gesamtTreffer} Treffer
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button type="button" variant="outline" onClick={() => load()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Neu laden
          </Button>
          <Button type="button" variant="outline" onClick={handleExport} disabled={exporting}>
            <Download className="w-4 h-4 mr-1.5" /> {exporting ? 'Erstelle Export…' : 'Excel-Export'}
          </Button>
          <Button
            type="button"
            onClick={handleJetztPruefen}
            disabled={isRunning}
            className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
          >
            <PlayCircle className="w-4 h-4 mr-1.5" /> {isRunning ? 'Laeuft…' : 'Jetzt pruefen'}
          </Button>
        </div>
      </div>

      {isRunning && progress && (
        <div className="rounded-lg border border-[#DAD9D9] bg-white p-3">
          <div className="h-2 bg-[#DAD9D9] rounded">
            <div className="h-2 bg-[#E9FF57] rounded" style={{ width: `${progress.percentage}%` }} />
          </div>
          <p className="text-sm mt-1 text-[#1A1A1A]">{progress.message}</p>
        </div>
      )}
      {jobError && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{jobError}</div>}

      {/* Filter-Leiste (LIST-01/02/06) */}
      <div className="rounded-lg border border-[#DAD9D9] bg-white p-4 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-bold text-gray-500 uppercase">Leichen-Typ</label>
          <select
            className="border border-[#DAD9D9] rounded-lg px-2 py-1.5 text-sm bg-white text-[#1A1A1A]"
            value={leichenTyp}
            onChange={(e) => setLeichenTyp(e.target.value as KandidatFilterState['leichenTyp'])}
          >
            {LEICHEN_TYP_OPTIONEN.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-bold text-gray-500 uppercase">Kategorie</label>
          <select
            className="border border-[#DAD9D9] rounded-lg px-2 py-1.5 text-sm bg-white text-[#1A1A1A]"
            value={kategorieId}
            onChange={(e) => setKategorieId(e.target.value)}
          >
            <option value="">Alle Kategorien</option>
            {kategorieOptionen.map((k) => (
              <option key={k.kategorieId === null ? 'null' : k.kategorieId} value={k.kategorieId === null ? '' : String(k.kategorieId)}>
                {k.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-bold text-gray-500 uppercase">Suche (Nummer/Name)</label>
          <Input
            value={suche}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSuche(e.target.value)}
            className="w-48"
            placeholder="z.B. as-1234"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-bold text-gray-500 uppercase">Aktiv-Status</label>
          <select
            className="border border-[#DAD9D9] rounded-lg px-2 py-1.5 text-sm bg-white text-[#1A1A1A]"
            value={aktivFilter}
            onChange={(e) => setAktivFilter(e.target.value as AktivFilter)}
          >
            {AKTIV_FILTER_OPTIONEN.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-bold text-gray-500 uppercase">Status</label>
          <select
            className="border border-[#DAD9D9] rounded-lg px-2 py-1.5 text-sm bg-white text-[#1A1A1A]"
            value={status}
            onChange={(e) => setStatus(e.target.value as KandidatFilterState['status'])}
          >
            {STATUS_OPTIONEN.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2 text-sm text-[#1A1A1A] pb-1.5">
          <input
            type="checkbox"
            checked={gesnoozteAnzeigen}
            onChange={(e) => setGesnoozteAnzeigen(e.target.checked)}
            className="h-4 w-4"
          />
          Gesnoozte anzeigen
        </label>
      </div>

      {/* Aktionsleiste (Bulk-Snooze/Whitelist, LIST-03/05, PROT-02; Deaktivieren/Loeschen nur Admin, ACT-01/02, PROT-05) */}
      {selectedCount > 0 && (
        <div className="rounded-lg border border-[#1A1A1A] bg-white p-3 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-sm text-[#1A1A1A] font-semibold">{selectedCount} ausgewaehlt</span>
          <div className="flex items-center gap-2 flex-wrap">
            <Button type="button" variant="outline" onClick={() => setSelected(new Map())}>
              Auswahl aufheben
            </Button>
            <Button type="button" variant="outline" onClick={openBulkSnooze}>
              Auswahl snoozen
            </Button>
            <Button type="button" onClick={openBulkWhitelist} className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]">
              Auswahl whitelisten
            </Button>
            {isAdmin && (
              <AktionenBar
                anzahl={selectedCount}
                disabled={isRunning}
                onDeaktivieren={handleDeaktivieren}
                onLoeschen={() => setLoeschDialogOpen(true)}
              />
            )}
          </div>
        </div>
      )}

      {actionError && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{actionError}</div>}
      {error && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {loading && <p className="text-sm text-gray-500">Wird geladen…</p>}

      {!loading && !error && kategorien.length === 0 && (
        <div className="rounded-lg border border-[#DAD9D9] bg-white p-8 text-center text-sm text-gray-500">
          Keine Treffer fuer die aktuelle Filterauswahl.
        </div>
      )}

      <div className="space-y-3">
        {kategorien.map((gruppe) => {
          const key = gruppe.kategorieId === null ? 'null' : String(gruppe.kategorieId);
          return (
            <KategorieGruppeComponent
              key={key}
              gruppe={gruppe}
              expanded={expandedGroups.has(key)}
              onToggleExpanded={() => toggleGroupExpanded(gruppe)}
              selectedIds={new Set(selected.keys())}
              onToggleItem={toggleItem}
              onToggleGroup={toggleGroupSelection}
              onSnoozeEinzeln={openEinzelSnooze}
              onAufheben={handleAufheben}
              onWhitelistEinzeln={openEinzelWhitelist}
            />
          );
        })}
      </div>

      <QueueStatusPanel isRunning={isRunning} onFortsetzen={handleFortsetzen} />

      <SnoozeDialog
        open={snoozeDialogOpen}
        onOpenChange={setSnoozeDialogOpen}
        anzahl={snoozeDialogItemIds.length}
        onConfirm={handleSnoozeConfirm}
      />
      <WhitelistDialog
        open={whitelistDialogOpen}
        onOpenChange={setWhitelistDialogOpen}
        anzahl={whitelistDialogItemIds.length}
        onConfirm={handleWhitelistConfirm}
      />
      <LoeschBestaetigungDialog
        open={loeschDialogOpen}
        onOpenChange={setLoeschDialogOpen}
        anzahl={selectedCount}
        onConfirm={handleLoeschenConfirm}
      />
    </div>
  );
}
